import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Wrench, Send, Users, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAdminNotifications } from '../lib/adminNotifications';

const ICONS = {
  service_request: Wrench,
  property_submission: Send,
  property_interest: Users,
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const { items, unread, markAllRead, markRead, removeNotification } = useAdminNotifications();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative w-9 h-9 rounded-full border border-gray-200 hover:border-[#9a1b2f] hover:bg-[#fef2f4] flex items-center justify-center transition-colors"
          aria-label={`Notifications (${unread} unread)`}
        >
          <Bell className="w-4 h-4 text-gray-700" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#9a1b2f] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 max-h-[500px] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="font-bold text-gray-900">Notifications</div>
            <div className="text-xs text-gray-500">{unread} unread of {items.length}</div>
          </div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs font-semibold text-[#9a1b2f] hover:underline flex items-center gap-1">
              <CheckCheck className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              You're all caught up!
            </div>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <div key={n.id} className={`group flex items-start gap-3 p-3 border-b border-gray-50 hover:bg-gray-50 transition ${n.read ? 'opacity-70' : 'bg-[#fef2f4]/40'}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${n.read ? 'bg-gray-100 text-gray-500' : 'bg-[#fef2f4] text-[#9a1b2f]'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {n.link ? (
                      <Link
                        to={n.link}
                        onClick={() => { setOpen(false); if (!n.read) markRead([n.id]); }}
                        className="block"
                      >
                        <div className="font-semibold text-gray-900 text-sm line-clamp-1 hover:text-[#9a1b2f]">{n.title}</div>
                        <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">{n.body}</div>
                      </Link>
                    ) : (
                      <div>
                        <div className="font-semibold text-gray-900 text-sm line-clamp-1">{n.title}</div>
                        <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">{n.body}</div>
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 mt-1">{relTime(n.created_at)}</div>
                  </div>
                  <button
                    onClick={() => removeNotification(n.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded hover:bg-gray-200 flex items-center justify-center text-gray-400"
                    title="Dismiss"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
